import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import {
  JobState,
  LoadState,
  putV1CompaniesCompanyIdLoadsLoadIdComplete,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode, useCallback, useRef, useState } from 'react';

import { Status as LoadStatus } from '~components/Job/LoadStatus';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import { Load } from '~hooks/useLoad/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { getLoadTransitionableStates } from '../utils';

interface LoadStatusTransitionerProps {
  closeOnTransitionComplete?: boolean;
  jobState: JobState;
  load: Load;
  isDisabled?: boolean;
  onLoadUpdate: (load: Load) => void;
}

export default function LoadStatusTransitioner({
  closeOnTransitionComplete,
  jobState,
  load,
  isDisabled = false,
  onLoadUpdate,
}: LoadStatusTransitionerProps) {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // Get transitionable states based on job state and load state
  const transitionableStates = getLoadTransitionableStates(load, jobState);

  const handleLoadStatusTransition = useCallback(
    async (state: LoadState) => {
      try {
        if (state === LoadState.COMPLETED) {
          const resp = await putV1CompaniesCompanyIdLoadsLoadIdComplete({
            path: { 'company-id': companyId, 'load-id': load.id },
          });
          const updatedLoad = Load.parse(resp.data.data);
          onLoadUpdate(updatedLoad);
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.load.failed_to_update_load_state'),
          [422],
        );
      }
    },
    [load.id, companyId, onLoadUpdate],
  );

  const handleMenuItemClick = useCallback(
    async (state: LoadState) => {
      if (isTransitioning) return;

      try {
        setIsTransitioning(true);
        await handleLoadStatusTransition(state);
      } finally {
        setIsTransitioning(false);
        if (closeOnTransitionComplete) {
          menuHandler.current?.onClose?.();
        }
      }
    },
    [isTransitioning, closeOnTransitionComplete, handleLoadStatusTransition],
  );

  if (transitionableStates.length === 0) {
    return <LoadStatus status={load.state} />;
  }

  return (
    <Menu
      ref={menuHandler}
      disableAutoFocusItem
      preventCloseCondition={isTransitioning}
      menuTrigger={
        <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>
          <LoadStatus status={load.state} hideDropdownArrow={false} />
        </Box>
      }
    >
      {transitionableStates.map((state) => (
        <LoadStatusTransitionerMenuItem
          key={state}
          isDisabled={isTransitioning || isDisabled}
          isLoading={isTransitioning}
          onClick={() => handleMenuItemClick(state)}
          text={t(`dispatch.job.mark_as_${state.toLowerCase()}`)}
          dataUi={`page-dispatch__load-status-transitioner__load-${load.id}__${state.toLowerCase()}-button`}
          leftIcon={
            <CheckCircleOutlineOutlined
              sx={{
                color: theme.brandV2.colors.treadGreen,
                fontSize: '15px',
              }}
            />
          }
        />
      ))}
    </Menu>
  );
}

interface LoadStatusTransitionerMenuItemProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  onClick: () => void;
  text: string;
  dataUi?: string;
}

function LoadStatusTransitionerMenuItem({
  isDisabled,
  isLoading,
  leftIcon,
  onClick,
  text,
  dataUi,
}: LoadStatusTransitionerMenuItemProps) {
  const theme = useTheme();

  return (
    <MenuItem
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();

        if (!isDisabled) {
          onClick();
        }
      }}
      data-ui={dataUi || 'page-dispatch__load-status-transitioner__menu-item'}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {leftIcon}

        <OverflowAwareText
          flex={1}
          sx={{
            color: isDisabled ? theme.brandV2.colors.treadGray6 : undefined,
          }}
        >
          {text}
        </OverflowAwareText>

        <LoadingSpinner
          isVisible={isLoading}
          loadingIndicatorSize={15}
          sx={{ flex: 0, minWidth: '15px' }}
        />
      </Box>
    </MenuItem>
  );
}
